/* Built In Imports */
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

/* External Imports */
import { createContext, useContext, useState } from 'react';
import { Box, Link, Text, Flex, Heading } from '@chakra-ui/react';
import { nanoid } from 'nanoid';
import Slider from 'react-slick';
import { FaArrowRight, FaArrowLeft, FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import SectionTitle from '@components/Headings/SectionTitle';

/* Internal Imports */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextContent from '@components/UI/StructuredTextContent';
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';
import { Styles } from '../../constants';
import FlowerDivider from '@components/Layout/FlowerDivider';

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <Box
      position="absolute"
      right={{ base: "0px", md: "-12px" }}
      top={{ base: "230px", md: "250px" }}
      transform="none"
      zIndex="2"
      cursor="pointer"
      bg="white"
      borderRadius="50%"
      w="36px"
      h="36px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow="0 2px 8px rgba(0,0,0,0.15)"
      _hover={{ bg: "#f5f5f5" }}
      onClick={onClick}
    >
      <FaChevronRight color="#6D6358" />
    </Box>
  );
};

const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <Box
      position="absolute"
      left={{ base: "0px", md: "-12px" }}
      top={{ base: "230px", md: "250px" }}
      transform="none"
      zIndex="2"
      cursor="pointer"
      bg="white"
      borderRadius="50%"
      w="36px"
      h="36px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxShadow="0 2px 8px rgba(0,0,0,0.15)"
      _hover={{ bg: "#f5f5f5" }}
      onClick={onClick}
    >
      <FaChevronLeft color="#6D6358" />
    </Box>
  );
};

// Create a context to manage accordion state
const AccordionContext = createContext({
  activeIds: [],
  setActiveIds: () => { },
});

const AccordionItem = ({ children, id, isFirstItem = false, headerRef }) => {
  const { activeIds, setActiveIds } = useContext(AccordionContext);
  const isOpen = activeIds.includes(id);

  const toggle = () => {
    if (isOpen) {
      // Remove this id from activeIds (close it)
      setActiveIds(activeIds.filter(activeId => activeId !== id));
    } else {
      // Add this id to activeIds (open it)
      setActiveIds([...activeIds, id]);

      // Smooth scroll to the header on mobile when opening
      if (headerRef?.current && window.innerWidth <= 768) {
        setTimeout(() => {
          headerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100); // Small delay to allow content to start expanding
      }
    }
  };

  return children(isOpen, toggle);
};

const AccordionContextSlider = ({ sectionContent, region, lang, id, isFirstItem = false, isLastItem = false, index = 0 }) => {
  const [activeIds, setActiveIds] = useState([]);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const headerRefs = useRef({});
  const [isDesktop, setIsDesktop] = useState(true);

  //console.log("OfferingsRitualsSlider sectionContent", sectionContent);

  // Extract offerings data from API structure - sectionContent is directly the section data
  const offeringsData = sectionContent?.sectionContent || [];

  //console.log("offeringsData:", offeringsData);

  // Group the content by deity (Naga, Yogeshwara Linga, Nandi)
  const groupedOfferings = [];
  let currentGroup = null;

  offeringsData.forEach((item) => {
    if (item._modelApiKey === 'single_line_text') {
      // This is a deity name header
      if (currentGroup) {
        groupedOfferings.push(currentGroup);
      }
      currentGroup = {
        deityName: item.text,
        cards: []
      };
    } else if (item._modelApiKey === 'card_info2_column_slider_group' && currentGroup) {
      // These are the cards for the current deity
      currentGroup.cards = item.cards || [];
    }
  });

  // Add the last group
  if (currentGroup) {
    groupedOfferings.push(currentGroup);
  }

  useEffect(() => {
  const handleResize = () => {
    setIsDesktop(window.innerWidth >= 1024);
  };

  handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set the first section (Naga) as active on initial render
  useEffect(() => {
    if (isFirstRender && groupedOfferings.length > 0) {
      setActiveIds([`${groupedOfferings[0].deityName}-0`]);
      setIsFirstRender(false);
    }
  }, [isFirstRender, groupedOfferings]);

  const router = useRouter();
  const { asPath: url } = router;

  const settings = {
    dots: true,
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: '40px',
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: '20px',
        }
      }
    ]
  };

  return (
    <AccordionContext.Provider value={{ activeIds, setActiveIds, isFirstRender }}>
      <Box
        maxW="900.87px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={8}
        position="relative"
      >
        {/* Main Title */}
        <Box textAlign="center" mb={8}>
          <SectionTitle titleObj={offeringsData[0]} />
        </Box>

        {/* Deity Sections */}
        {groupedOfferings.map((group, groupIndex) => {
          const accordionId = `${group.deityName}-${groupIndex}`;
          const showArrows =
            (isDesktop && group.cards.length > 2) ||
            (!isDesktop && group.cards.length > 1);

          const sliderSettings = {
            ...settings,
            arrows: showArrows,
            nextArrow: showArrows ? <NextArrow /> : null,
            prevArrow: showArrows ? <PrevArrow /> : null,
          };

          return (
            <Box key={accordionId}>
              <AccordionItem
                id={accordionId}
                isFirstItem={groupIndex === 0}
                headerRef={headerRefs.current[accordionId]}
              >
                {(isOpen, toggle) => (
                  <Box
                    borderRadius="lg"
                    overflow="hidden"
                    bg="transparent"
                  >
                    {/* Deity Header */}
                    <Box
                      ref={(el) => {
                        if (el) {
                          headerRefs.current[accordionId] = el;
                        }
                      }}
                      onClick={toggle}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      cursor="pointer"
                      px={0}
                      pt={2}
                      pb={1}
                      bg="transparent"
                      _hover={{ bg: "transparent" }}
                      transition="background-color 0.2s"
                      scrollMarginTop={{ base: "20px", md: "0px" }}
                    >
                      <Heading
                        as="h3"
                        fontFamily="FedraSansStd-book"
                        fontSize="18px"
                        fontWeight="600"
                        lineHeight="30px"
                        letterSpacing="0%"
                        color="#28231E"
                      >
                        {group.deityName}
                      </Heading>
                      <Box
                        transform={isOpen ? 'rotate(180deg)' : 'rotate(0)'}
                        transition="transform 0.3s ease-in-out"
                      >
                        <FaChevronDown color="#6D6358" />
                      </Box>
                    </Box>

                    {/* Cards Slider */}
                    <Box
                      position="relative"
                      overflow="hidden"
                      transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                      maxHeight={isOpen ? '2000px' : '0'}
                      opacity={isOpen ? 1 : 0}
                      transform={isOpen ? 'translateY(0)' : 'translateY(-10px)'}
                    >
                      <Box px={{ base: 4, md: 8 }} pt={3} pb={6}>

                        <Box className={`offerings-slider-${groupIndex}`}>
                          <Slider
                            {...sliderSettings}
                            style={{
                              padding: '0 10px',
                              margin: '0 -10px'
                            }}
                          >
                            {group.cards.map((card, cardIndex) => (
                              <Box key={`${card.title}-${cardIndex}`} px={{ base: 2, md: 3 }} py={2}>
                                <Box
                                  bg="white"
                                  borderRadius="lg"
                                  overflow="hidden"
                                  display="flex"
                                  flexDirection="column"
                                  minHeight="420px"
                                >
                                  {/* Card Image */}
                                  <Box
                                    position="relative"
                                    width="200px"
                                    height="200px"
                                    overflow="hidden"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    borderRadius="50%"
                                    mx="auto"
                                    mt={6}
                                    mb={0}
                                  >
                                    <LazyLoadImageComponent
                                      src={card.thumbnail?.url}
                                      alt={card.thumbnail?.alt || card.title}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                        borderRadius: '50%'
                                      }}
                                    />
                                  </Box>

                                  {/* Card Content */}
                                  <Box
                                    pt={{ base: 2, md: 3 }}
                                    px={{ base: 4, md: 6 }}
                                    pb={{ base: 4, md: 6 }}
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    textAlign="center"
                                    alignItems="center"
                                  >
                                    <Heading
                                      as="h4"
                                      fontFamily="FedraSerifAStdBook"
                                      fontSize="22px"
                                      fontWeight="600"
                                      mb={1}
                                      color="#28231E"
                                      lineHeight={{ base: "120%", md: "100%" }}
                                      letterSpacing="0%"
                                      textAlign="center"
                                      minHeight={{ base: "auto", md: "50px" }}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      whiteSpace={{ base: "normal", md: "nowrap" }}
                                      overflow={{ base: "visible", md: "hidden" }}
                                      textOverflow={{ base: "unset", md: "ellipsis" }}
                                      width="100%"
                                      px={{ base: 2, md: 0 }}
                                    >
                                      {card.title}
                                    </Heading>

                                    <Text
                                      fontFamily="FedraSansStd-book"
                                      fontSize="16px"
                                      fontWeight="350"
                                      fontStyle="Book"
                                      color="#676055"
                                      mb={4}
                                      lineHeight="24px"
                                      letterSpacing="0%"
                                      textAlign="center"
                                      flex="1"
                                    >
                                      {StructuredTextContent(card.description)}
                                    </Text>

                                    {/* Button */}
                                    {card.buttonText && card.buttonLink && (
                                      <NextLink
                                        href={refineUrlDomain(card.buttonLink, region, lang)}
                                        passHref
                                        legacyBehavior
                                      >
                                        <Link
                                          display="inline-flex"
                                          alignItems="center"
                                          bg="transparent"
                                          color="#CF4520"
                                          border="1px solid #CF4520"
                                          px={6}
                                          py={3}
                                          borderRadius="3.45px"
                                          fontFamily="FedraSansStd-book"
                                          fontSize="16px"
                                          fontWeight="500"
                                          lineHeight="24px"
                                          letterSpacing="0%"
                                          textAlign="center"
                                          _hover={{
                                            textDecoration: "none",
                                            bg: "#CF4520",
                                            color: "white",
                                            transform: "translateY(-2px)",
                                            boxShadow: "md"
                                          }}
                                          _active={{
                                            transform: "translateY(0)",
                                            boxShadow: "sm"
                                          }}
                                          transition="all 0.2s"
                                          width="fit-content"
                                        >
                                          {card.buttonText}
                                        </Link>
                                      </NextLink>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Slider>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </AccordionItem>

              {/* Border line - only show if not the last section */}
              {groupIndex < groupedOfferings.length - 1 && (
                <Box
                  w="100%"
                  h="1px"
                  bg="#C5BFB1"
                  mt={2}
                  mb={0}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <style jsx global>{`
        .accordion-item {
          scroll-behavior: smooth;
        }
        .offerings-slider-0 .slick-dots,
        .offerings-slider-1 .slick-dots,
        .offerings-slider-2 .slick-dots {
          position: relative;
          bottom: 0;
          margin-top: 24px;
        }
        .offerings-slider-0 .slick-dots li,
        .offerings-slider-1 .slick-dots li,
        .offerings-slider-2 .slick-dots li {
          margin: 0 -3px;
        }
        .offerings-slider-0 .slick-dots li button:before,
        .offerings-slider-1 .slick-dots li button:before,
        .offerings-slider-2 .slick-dots li button:before {
          font-size: 10px;
          opacity: 0.5;
          color: #D3D3D3;
        }
        .offerings-slider-0 .slick-dots li.slick-active button:before,
        .offerings-slider-1 .slick-dots li.slick-active button:before,
        .offerings-slider-2 .slick-dots li.slick-active button:before {
          opacity: 1;
          color: #858585;
        }
      `}</style>
    </AccordionContext.Provider>
  );
};

export default AccordionContextSlider;
